/**
 * TanStack Query provider component
 *
 * Wraps the application with QueryClientProvider and
 * development tools for debugging query state.
 */

import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import type { ReactNode } from "react";

import { queryClient } from "@/lib/query-client";

interface QueryProviderProps {
  children: ReactNode;
}

/**
 * Query provider wrapper for the application
 *
 * Provides TanStack Query context to all child components
 * and includes devtools in development mode only.
 *
 * @example
 * ```tsx
 * <QueryProvider>
 *   <App />
 * </QueryProvider>
 * ```
 *
 * Integration note: Wrap this provider around your router or
 * inside your router's root layout, depending on your setup:
 *
 * With TanStack Router (recommended):
 * - Create the provider in your root route's component
 * - Or wrap the RouterProvider in main.tsx
 */
export function QueryProvider({ children }: QueryProviderProps) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}
